import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dumbbell, MapPin, Phone, Mail, Clock, CheckCircle, ArrowRight, Instagram, Facebook, Users, HeartPulse, Trophy, Quote, Star, ShieldCheck, Activity } from 'lucide-react';

// === Main Component ===
const MidGymLanding = () => {
  const [currentPage, setCurrentPage] = useState('home');

  // Page Transition Animation
  const pageVariants = {
    initial: { opacity: 0, y: 15 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -15 }
  };

  return (
    <div className="bg-white text-neutral-900 min-h-screen font-sans selection:bg-red-600 selection:text-white flex flex-col overflow-x-hidden">
      
      {/* Navigation Bar */}
      <nav className="bg-neutral-950 text-white sticky top-0 z-50 shadow-lg border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex justify-between items-center">
          <div 
            onClick={() => setCurrentPage('home')}
            className="flex items-center gap-2 text-2xl font-black text-red-600 tracking-tighter uppercase cursor-pointer"
          >
            <Dumbbell size={32} />
            Iron<span className="text-white">Forge</span>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex gap-8 font-bold uppercase text-sm tracking-wider">
            {['home', 'about', 'services', 'contact'].map((item) => (
              <button 
                key={item}
                onClick={() => setCurrentPage(item)}
                className={`hover:text-red-500 transition-colors ${currentPage === item ? 'text-red-600 border-b-2 border-red-600 pb-1' : 'text-neutral-400'}`}
              >
                {item}
              </button>
            ))}
          </div>
          
          {/* Get Pass ab naye page pe jayega */}
          <button 
            onClick={() => setCurrentPage('getpass')} 
            className="bg-red-600 text-white font-bold py-2 px-6 hover:bg-red-700 transition-all shadow-[0_0_15px_rgba(220,38,38,0.4)]"
          >
            Get Pass
          </button>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="flex-grow">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.3 }}
          >
            {currentPage === 'home' && <HomeView setPage={setCurrentPage} />}
            {currentPage === 'about' && <AboutView />}
            {currentPage === 'services' && <ServicesView setPage={setCurrentPage} />}
            {currentPage === 'contact' && <ContactView />}
            {currentPage === 'getpass' && <GetPassView />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-neutral-950 text-neutral-400 py-12 border-t-4 border-red-600 mt-auto">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2 text-2xl font-black text-red-600 tracking-tighter uppercase mb-4">
              <Dumbbell size={24} /> Iron<span className="text-white">Forge</span>
            </div>
            <p className="text-sm leading-relaxed mb-6 max-w-sm">A premium fitness facility dedicated to helping you achieve your physical best. State-of-the-art equipment and expert coaching for all fitness levels.</p>
            <div className="flex gap-4">
              <div className="bg-neutral-800 p-2 rounded-full hover:bg-red-600 hover:text-white transition-colors cursor-pointer"><Facebook size={18} /></div>
              <div className="bg-neutral-800 p-2 rounded-full hover:bg-red-600 hover:text-white transition-colors cursor-pointer"><Instagram size={18} /></div>
            </div>
          </div>
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li className="cursor-pointer hover:text-red-500 flex items-center gap-2" onClick={() => setCurrentPage('home')}><ArrowRight size={14}/> Home</li>
              <li className="cursor-pointer hover:text-red-500 flex items-center gap-2" onClick={() => setCurrentPage('about')}><ArrowRight size={14}/> Our Story</li>
              <li className="cursor-pointer hover:text-red-500 flex items-center gap-2" onClick={() => setCurrentPage('services')}><ArrowRight size={14}/> Programs</li>
              <li className="cursor-pointer hover:text-red-500 flex items-center gap-2" onClick={() => setCurrentPage('getpass')}><ArrowRight size={14}/> Membership Plans</li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6">Contact Info</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3"><MapPin size={18} className="text-red-600 shrink-0"/> <span>78 Strength Avenue, City, 201002</span></li>
              <li className="flex items-center gap-3"><Phone size={18} className="text-red-600 shrink-0"/> <span>+91 98765 43211</span></li>
              <li className="flex items-center gap-3"><Mail size={18} className="text-red-600 shrink-0"/> <span>hello@ironforge.demo</span></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-12 pt-6 border-t border-neutral-800 text-center text-xs">
          © {new Date().getFullYear()} IronForge Fitness. Built for Demo purposes.
        </div>
      </footer>
    </div>
  );
};

// ==========================================
// 1. HOME PAGE VIEW (Expanded)
// ==========================================
const HomeView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div>
    {/* Hero Banner */}
    <section className="relative h-[85vh] flex items-center justify-center text-center">
      <div className="absolute inset-0 z-0 bg-neutral-900">
        <img src="https://images.unsplash.com/photo-1540497077202-7c8a3999166f?q=80&w=1470&auto=format&fit=crop" alt="Gym" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-neutral-900/50"></div>
      </div>
      <div className="relative z-10 max-w-4xl px-6">
        <div className="inline-block bg-red-600 text-white font-bold px-4 py-1 mb-6 text-sm tracking-widest uppercase">Start Your Journey Today</div>
        <h1 className="text-5xl md:text-8xl font-black text-white uppercase tracking-tight mb-6 leading-none">
          Forged in <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-700">Iron.</span><br/> Built for Life.
        </h1>
        <p className="text-lg md:text-xl text-neutral-300 mb-10 max-w-2xl mx-auto font-light">Join the city's premier fitness center. We provide the space, the tools, and the expert guidance you need to dominate your goals.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={() => setPage('getpass')} className="bg-red-600 text-white font-bold text-lg py-4 px-10 hover:bg-red-700 transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(220,38,38,0.5)]">
            View Memberships <ArrowRight size={20} />
          </button>
          <button onClick={() => setPage('about')} className="bg-white/10 backdrop-blur-md text-white border border-white/20 font-bold text-lg py-4 px-10 hover:bg-white/20 transition-all">
            Discover Our Gym
          </button>
        </div>
      </div>
    </section>

    {/* 3-Pillar Features */}
    <section className="py-20 px-6 max-w-7xl mx-auto bg-white">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 -mt-32 relative z-20">
        {[
          { icon: <Dumbbell size={40}/>, title: "Pro Equipment", desc: "Hammer Strength & LifeFitness machines imported directly for maximum muscle engagement." },
          { icon: <Users size={40}/>, title: "Expert Trainers", desc: "Certified coaches to guide your journey, correct your form, and push your limits." },
          { icon: <HeartPulse size={40}/>, title: "Group Classes", desc: "Yoga, HIIT, and Spin classes daily to keep your cardio routines fun and engaging." }
        ].map((item, i) => (
          <div key={i} className="bg-white p-10 shadow-2xl border-t-4 border-red-600 text-center hover:-translate-y-2 transition-transform duration-300">
            <div className="text-red-600 mb-6 flex justify-center bg-red-50 w-20 h-20 mx-auto items-center rounded-full">{item.icon}</div>
            <h3 className="text-2xl font-black uppercase mb-3">{item.title}</h3>
            <p className="text-neutral-500 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </section>

    {/* NEW: Why Choose Us Section */}
    <section className="py-24 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <h2 className="text-4xl font-black uppercase text-neutral-900 mb-2">Why Train <span className="text-red-600">With Us?</span></h2>
          <div className="w-20 h-1 bg-red-600 mb-8"></div>
          <p className="text-neutral-600 text-lg mb-8 leading-relaxed">
            We don't just sell memberships; we deliver results. Our facility is engineered to provide the best workout experience without the typical gym crowding.
          </p>
          <ul className="space-y-6">
            {[
              { icon: <ShieldCheck size={24}/>, title: "Hygienic Environment", text: "Air purifiers, sanitized stations, and spotless locker rooms." },
              { icon: <Activity size={24}/>, title: "Free Body Composition Analysis", text: "Track your muscle mass and body fat percentage monthly." },
              { icon: <Clock size={24}/>, title: "Extended Working Hours", text: "Open early morning to late night, fitting your busy schedule." }
            ].map((feature, idx) => (
              <li key={idx} className="flex items-start gap-4">
                <div className="text-red-600 mt-1">{feature.icon}</div>
                <div>
                  <h4 className="font-bold text-xl text-neutral-900 mb-1">{feature.title}</h4>
                  <p className="text-neutral-500">{feature.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1574680096145-d05b474e2155?q=80&w=1000&auto=format&fit=crop" alt="Gym Workout" className="rounded-lg shadow-2xl relative z-10" />
          <div className="absolute -bottom-8 -left-8 w-full h-full border-4 border-red-600 rounded-lg z-0"></div>
        </div>
      </div>
    </section>

    {/* NEW: Testimonials Section */}
    <section className="py-24 bg-neutral-950 text-white">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-4xl font-black uppercase mb-2">Success <span className="text-red-600">Stories</span></h2>
        <div className="w-20 h-1 bg-red-600 mx-auto mb-16"></div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { name: "Rahul D.", text: "Lost 15kgs in 4 months. The trainers here don't just count reps, they educate you on diet and lifestyle.", rating: 5 },
            { name: "Sneha M.", text: "Best equipment in the city. The Zumba classes are super energetic and the crowd is very decent.", rating: 5 },
            { name: "Amit K.", text: "Switched from a luxury gym to IronForge. Better vibe, heavier weights, and the owners actually care.", rating: 4 }
          ].map((review, idx) => (
            <div key={idx} className="bg-neutral-900 p-8 text-left border border-neutral-800 relative">
              <Quote size={40} className="text-neutral-800 absolute top-4 right-4" />
              <div className="flex gap-1 text-red-500 mb-6">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill={i < review.rating ? "currentColor" : "none"} />)}
              </div>
              <p className="text-neutral-300 italic mb-6">"{review.text}"</p>
              <h4 className="font-bold text-white uppercase tracking-wider">- {review.name}</h4>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// ==========================================
// 2. ABOUT PAGE VIEW (Expanded Text)
// ==========================================
const AboutView = () => (
  <div className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-black uppercase text-neutral-900 mb-4">Our <span className="text-red-600">Story</span></h2>
        <div className="w-24 h-1 bg-red-600 mx-auto"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-24">
        {/* Expanded Text Section */}
        <div className="space-y-6">
          <h3 className="text-3xl font-black uppercase mb-6 text-neutral-900">More than just a gym. <br/><span className="text-red-600">A Fitness Sanctuary.</span></h3>
          
          <p className="text-neutral-600 text-lg leading-relaxed">
            Established in 2015, IronForge was built on a simple premise: fitness should be accessible, professional, and entirely result-oriented. We moved away from the crowded, intimidating gym models to create a space where beginners and professional athletes can train side by side without ego.
          </p>
          
          <p className="text-neutral-600 text-lg leading-relaxed">
            Our 10,000 sq ft facility is engineered to perfection. We realized that local gyms either lacked good equipment or had poor hygiene. So, we equipped IronForge with top-tier ventilation systems, hospital-grade sanitized locker rooms, and an environment that screams motivation the moment you walk in.
          </p>

          <p className="text-neutral-600 text-lg leading-relaxed">
            But machines don't build bodies—people do. Our founding team consists of national-level athletes and certified nutritionists who rigorously train our staff. When you join us, you aren't just renting treadmills; you are joining a community that holds you accountable to your goals.
          </p>
          
          <div className="pt-6 border-t border-neutral-200 grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-3xl font-black text-red-600">8+</p>
              <p className="text-xs uppercase font-bold text-neutral-500 mt-1">Years Active</p>
            </div>
            <div>
              <p className="text-3xl font-black text-red-600">2000+</p>
              <p className="text-xs uppercase font-bold text-neutral-500 mt-1">Transformations</p>
            </div>
            <div>
              <p className="text-3xl font-black text-red-600">15+</p>
              <p className="text-xs uppercase font-bold text-neutral-500 mt-1">Pro Coaches</p>
            </div>
          </div>
        </div>
        
        {/* Image Grid for better visual balance */}
        <div className="grid grid-cols-2 gap-4 h-full">
          <img src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=600&auto=format&fit=crop" alt="Gym Floor" className="rounded-lg shadow-xl w-full h-64 md:h-full object-cover" />
          <div className="grid grid-rows-2 gap-4">
             <img src="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?q=80&w=600&auto=format&fit=crop" alt="Weights" className="rounded-lg shadow-xl w-full h-32 md:h-full object-cover" />
             <img src="https://images.unsplash.com/photo-1518611012118-696072aa579a?q=80&w=600&auto=format&fit=crop" alt="Cardio" className="rounded-lg shadow-xl w-full h-32 md:h-full object-cover" />
          </div>
        </div>
      </div>

      {/* Team Section (Images Fixed) */}
      <div className="text-center mb-16 border-t pt-16">
        <h3 className="text-4xl font-black uppercase mb-2">Meet The <span className="text-red-600">Trainers</span></h3>
        <p className="text-neutral-500 max-w-2xl mx-auto">Certified experts dedicated to pushing your limits safely.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { name: "Vikram Singh", role: "Head Coach & Founder", img: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?q=80&w=500&auto=format&fit=crop" },
          { name: "Priya Sharma", role: "Yoga & Core Specialist", img: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=500&auto=format&fit=crop" },
          { name: "Rahul Verma", role: "Strength & Conditioning", img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=500&auto=format&fit=crop" } // Reliable image
        ].map((trainer, i) => (
          <div key={i} className="group relative overflow-hidden bg-neutral-100 rounded-lg shadow-lg">
            <img src={trainer.img} alt={trainer.name} className="w-full h-[400px] object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
            <div className="absolute bottom-0 w-full bg-gradient-to-t from-neutral-950 via-neutral-900/80 to-transparent p-6 pt-12 text-center transform translate-y-4 group-hover:translate-y-0 transition-transform">
              <h4 className="text-white font-bold text-xl mb-1">{trainer.name}</h4>
              <p className="text-red-500 font-medium text-sm uppercase tracking-wider">{trainer.role}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ==========================================
// 3. SERVICES PAGE VIEW (Focused on Programs)
// ==========================================
const ServicesView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div className="py-20 bg-neutral-50 min-h-screen">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-black uppercase text-neutral-900 mb-4">Our <span className="text-red-600">Programs</span></h2>
        <div className="w-24 h-1 bg-red-600 mx-auto"></div>
        <p className="mt-6 text-neutral-600 max-w-2xl mx-auto text-lg">We offer a variety of programs tailored to different fitness levels and goals. From lifting heavy to finding inner peace.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
        {[
          { icon: <Trophy size={40}/>, title: "Personal Training", desc: "1-on-1 coaching designed specifically for your body type, goals, and medical history. Includes custom diet charting.", features: ["Goal-oriented workouts", "Weekly body analysis", "24/7 Trainer WhatsApp Support"] },
          { icon: <HeartPulse size={40}/>, title: "Cardio & HIIT", desc: "High-energy sessions perfect for fat loss and cardiovascular endurance. Burn up to 600 calories per session.", features: ["Latest Treadmills & Rowers", "Circuit Training Areas", "Stamina Building"] },
          { icon: <Activity size={40}/>, title: "Strength & Conditioning", desc: "Build pure muscle mass and core strength using our extensive free-weights section and plate-loaded machines.", features: ["Olympic Lifting Platforms", "Heavy Dumbbell Zone", "Squat Racks"] },
          { icon: <Users size={40}/>, title: "Group Zumba", desc: "Dance your weight off! A fun, community-driven cardio workout that feels more like a party than a gym session.", features: ["Certified Instructors", "Great Music & Vibe", "Stress Buster"] },
          { icon: <Star size={40}/>, title: "Power Yoga", desc: "Improve flexibility, fix your posture, and find mental clarity with our guided early morning and evening yoga batches.", features: ["Breathing Exercises", "Core Strengthening", "Calm Studio Environment"] }
        ].map((prog, i) => (
          <div key={i} className="bg-white p-8 shadow-lg border-t-4 border-neutral-900 hover:border-red-600 transition-colors">
            <div className="text-red-600 mb-6">{prog.icon}</div>
            <h3 className="text-2xl font-black uppercase mb-4">{prog.title}</h3>
            <p className="text-neutral-600 mb-6 min-h-[80px]">{prog.desc}</p>
            <ul className="space-y-3 text-sm font-bold text-neutral-700 border-t pt-6">
              {prog.features.map((f, j) => (
                <li key={j} className="flex items-center gap-3"><CheckCircle size={16} className="text-red-600"/> {f}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="bg-neutral-900 text-white rounded-xl p-10 text-center shadow-2xl">
        <h3 className="text-3xl font-black uppercase mb-4">Ready to sweat?</h3>
        <p className="text-neutral-400 mb-8 max-w-xl mx-auto">Check out our membership plans to find the right package for your selected program.</p>
        <button onClick={() => setPage('getpass')} className="bg-red-600 text-white font-bold py-4 px-10 hover:bg-red-700 transition-colors inline-flex items-center gap-2">
          See Pricing & Plans <ArrowRight size={20} />
        </button>
      </div>
    </div>
  </div>
);

// ==========================================
// 4. GET PASS PAGE VIEW (NEW: 6 Pricing Plans)
// ==========================================
const GetPassView = () => (
  <div className="py-20 bg-neutral-100 min-h-screen">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-black uppercase text-neutral-900 mb-4">Membership <span className="text-red-600">Passes</span></h2>
        <div className="w-24 h-1 bg-red-600 mx-auto"></div>
        <p className="mt-6 text-neutral-600 max-w-2xl mx-auto text-lg">Transparent pricing. No hidden registration fees. Choose the commitment that suits you.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        
        {/* 1. Day Pass */}
        <div className="bg-white p-8 rounded-xl shadow-md border border-neutral-200 flex flex-col">
          <h3 className="text-xl font-bold text-neutral-500 uppercase tracking-wider mb-2">1-Day Trial</h3>
          <div className="text-4xl font-black text-neutral-900 mb-6">₹199<span className="text-sm font-medium text-neutral-500">/day</span></div>
          <ul className="space-y-4 text-neutral-600 mb-8 flex-grow">
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Full Day Gym Access</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Try All Equipment</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Locker Usage</li>
          </ul>
          <button className="w-full py-3 font-bold border-2 border-neutral-300 text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition-colors uppercase">Buy Day Pass</button>
        </div>

        {/* 2. Monthly */}
        <div className="bg-white p-8 rounded-xl shadow-md border border-neutral-200 flex flex-col">
          <h3 className="text-xl font-bold text-neutral-500 uppercase tracking-wider mb-2">Monthly</h3>
          <div className="text-4xl font-black text-neutral-900 mb-6">₹1,499<span className="text-sm font-medium text-neutral-500">/month</span></div>
          <ul className="space-y-4 text-neutral-600 mb-8 flex-grow">
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Unlimited Gym Access</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> General Floor Guidance</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Free BMI Check</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Changing Room & Showers</li>
          </ul>
          <button className="w-full py-3 font-bold border-2 border-neutral-300 text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition-colors uppercase">Select Monthly</button>
        </div>

        {/* 3. Quarterly */}
        <div className="bg-white p-8 rounded-xl shadow-md border border-neutral-200 flex flex-col">
          <h3 className="text-xl font-bold text-neutral-500 uppercase tracking-wider mb-2">Quarterly (3 Months)</h3>
          <div className="text-4xl font-black text-neutral-900 mb-2">₹3,999</div>
          <p className="text-green-600 font-bold text-sm mb-6">Save ₹498</p>
          <ul className="space-y-4 text-neutral-600 mb-8 flex-grow">
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Everything in Monthly</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Basic Diet Chart</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> 1 Group Class/Week Free</li>
          </ul>
          <button className="w-full py-3 font-bold border-2 border-neutral-300 text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition-colors uppercase">Select Quarterly</button>
        </div>

        {/* 4. Half-Yearly */}
        <div className="bg-white p-8 rounded-xl shadow-md border border-neutral-200 flex flex-col">
          <h3 className="text-xl font-bold text-neutral-500 uppercase tracking-wider mb-2">Elite (6 Months)</h3>
          <div className="text-4xl font-black text-neutral-900 mb-2">₹6,999</div>
          <p className="text-green-600 font-bold text-sm mb-6">Save ₹1,995</p>
          <ul className="space-y-4 text-neutral-600 mb-8 flex-grow">
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Everything in Quarterly</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> 2 Free PT Sessions</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-green-500"/> Membership Pause (Up to 15 days)</li>
          </ul>
          <button className="w-full py-3 font-bold border-2 border-neutral-300 text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition-colors uppercase">Select Elite</button>
        </div>

        {/* 5. Yearly (HIGHLIGHTED) */}
        <div className="bg-neutral-950 p-8 rounded-xl shadow-2xl border-2 border-red-600 flex flex-col relative transform lg:-translate-y-4">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-1 font-black text-sm uppercase tracking-widest rounded-full">Most Popular</div>
          <h3 className="text-xl font-bold text-red-500 uppercase tracking-wider mb-2">Beast (12 Months)</h3>
          <div className="text-4xl font-black text-white mb-2">₹11,999</div>
          <p className="text-red-400 font-bold text-sm mb-6">Save ₹5,989 (Get 4 Months FREE!)</p>
          <ul className="space-y-4 text-neutral-300 mb-8 flex-grow">
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-red-500"/> Everything in Elite</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-red-500"/> Advanced Custom Diet Plan</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-red-500"/> Unlimited Group Classes</li>
            <li className="flex items-center gap-3"><CheckCircle size={18} className="text-red-500"/> Bring a Guest (2x a month)</li>
          </ul>
          <button className="w-full py-4 font-black bg-red-600 text-white hover:bg-red-700 transition-colors uppercase shadow-lg shadow-red-600/30">Become a Beast</button>
        </div>

        {/* 6. PT Add-on */}
        <div className="bg-gradient-to-br from-neutral-100 to-neutral-200 p-8 rounded-xl shadow-md border border-neutral-300 flex flex-col">
          <h3 className="text-xl font-bold text-neutral-800 uppercase tracking-wider mb-2">Personal Training</h3>
          <div className="text-4xl font-black text-neutral-900 mb-6">₹4,000<span className="text-sm font-medium text-neutral-600">/month add-on</span></div>
          <ul className="space-y-4 text-neutral-700 mb-8 flex-grow font-medium">
            <li className="flex items-center gap-3"><Activity size={18} className="text-neutral-900"/> Dedicated Coach 1-on-1</li>
            <li className="flex items-center gap-3"><Activity size={18} className="text-neutral-900"/> Form Correction & Spotting</li>
            <li className="flex items-center gap-3"><Activity size={18} className="text-neutral-900"/> Injury Rehab Support</li>
            <li className="flex items-center gap-3"><Activity size={18} className="text-neutral-900"/> Grocery Shopping List</li>
          </ul>
          <button className="w-full py-3 font-bold bg-neutral-900 text-white hover:bg-black transition-colors uppercase">Inquire for PT</button>
        </div>

      </div>
    </div>
  </div>
);

// ==========================================
// 5. CONTACT PAGE VIEW
// ==========================================
const ContactView = () => (
  <div className="py-20 max-w-7xl mx-auto px-6 min-h-[80vh]">
    <div className="text-center mb-16">
      <h2 className="text-4xl font-black uppercase text-neutral-900 mb-4">Get In <span className="text-red-600">Touch</span></h2>
      <div className="w-24 h-1 bg-red-600 mx-auto"></div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      {/* Contact Info & Hours */}
      <div className="space-y-8">
        <div className="bg-neutral-50 p-8 border-l-4 border-red-600 shadow-md">
          <h3 className="text-2xl font-black uppercase mb-6 text-neutral-900">Facility Details</h3>
          <ul className="space-y-6">
            <li className="flex items-start gap-4">
              <div className="bg-red-100 p-3 rounded-full text-red-600"><MapPin size={24}/></div>
              <div>
                <p className="font-bold text-neutral-900 text-lg">Location</p>
                <p className="text-neutral-600 mt-1">78 Strength Avenue, First Floor, Above Reliance Smart, City, 201002</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="bg-red-100 p-3 rounded-full text-red-600"><Phone size={24}/></div>
              <div>
                <p className="font-bold text-neutral-900 text-lg">Phone</p>
                <p className="text-neutral-600 mt-1">+91 98765 43211</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="bg-neutral-900 text-white p-8 rounded-lg shadow-xl">
          <h3 className="text-xl font-black uppercase mb-6 flex items-center gap-3"><Clock className="text-red-600"/> Operating Hours</h3>
          <ul className="space-y-4 text-neutral-300 font-medium">
            <li className="flex justify-between border-b border-neutral-700 pb-3"><span>Monday - Saturday</span> <span className="text-white">5:30 AM - 10:30 PM</span></li>
            <li className="flex justify-between pt-1"><span>Sunday</span> <span className="text-red-500">7:00 AM - 1:00 PM</span></li>
          </ul>
        </div>
      </div>

      {/* Corporate Contact Form */}
      <div className="bg-white p-10 shadow-2xl border border-neutral-100 rounded-lg">
        <h3 className="text-2xl font-black uppercase mb-2 text-neutral-900">Drop an Inquiry</h3>
        <p className="text-neutral-500 mb-8">Fill out the form below and our front desk will call you back shortly.</p>
        <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); alert('Demo Message Sent!'); }}>
          <div>
            <label className="block text-sm font-bold text-neutral-700 mb-2 uppercase tracking-wide">Full Name</label>
            <input type="text" required className="w-full bg-neutral-50 border border-neutral-300 p-4 rounded-md focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-all" placeholder="John Doe" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-bold text-neutral-700 mb-2 uppercase tracking-wide">Phone</label>
              <input type="text" required className="w-full bg-neutral-50 border border-neutral-300 p-4 rounded-md focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-all" placeholder="+91 00000 00000" />
            </div>
            <div>
              <label className="block text-sm font-bold text-neutral-700 mb-2 uppercase tracking-wide">Interest</label>
              <select className="w-full bg-neutral-50 border border-neutral-300 p-4 rounded-md focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-all text-neutral-700">
                <option>General Gym Membership</option>
                <option>Personal Training Details</option>
                <option>Group Classes / Yoga</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-neutral-700 mb-2 uppercase tracking-wide">Message (Optional)</label>
            <textarea rows={4} className="w-full bg-neutral-50 border border-neutral-300 p-4 rounded-md focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-all resize-none" placeholder="How can we help you reach your goals?"></textarea>
          </div>
          <button type="submit" className="w-full bg-red-600 text-white font-black py-4 rounded-md hover:bg-red-700 transition-colors uppercase tracking-widest mt-4 shadow-lg shadow-red-600/30">
            Send Message
          </button>
        </form>
      </div>
    </div>
  </div>
);

export default MidGymLanding;